import { Flex } from '@chakra-ui/react';
import { Modal, Button, useToast, Checkbox } from '@morressier/alembic';
import { useSubmittingAuthorOnlySubmitICMJEForm } from 'src/api/commands';
import { IDecisionModalPropsForSubmittingAuthor } from '../../../decisionModals/decisionModalHelper.js';
import { FormProvider, useForm } from 'react-hook-form';
import { useWorkflow } from 'src/hooks/useWorkflow';
import { CustomFormFieldDefinition } from '@publishing-engine/domain/types/article';
import { CustomFormFieldComponent } from 'src/components/FormField';
import { GenericFormFields } from '@publishing-engine/submission-service/src/workflow-configuration/generic-form-fields/generic-form-fields';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { generateValidationSchema } from '../../../../../utils/validation.js';
import { ajvResolver } from '@hookform/resolvers/ajv';

export const AuthorUploadICMJEFormForAll = ({
  onClose,
  submissionId,
  submissionContextId,
  authorEmail = '',
  authorStatus = true,
  icmjeFormList
}: IDecisionModalPropsForSubmittingAuthor) => {
  const {
    forms: { authorUploadICMJE },
    articleWorkflow
  } = useWorkflow();

  if (!authorUploadICMJE) {
    return <></>;
  }

  const toast = useToast();

  const { refetchSubmission, organizationId } = useSubmissionData();
  const authorUploadICMJEMutation = useSubmittingAuthorOnlySubmitICMJEForm({
    submissionId,
    organizationId,
    submissionContextId,
    articleWorkflowId: articleWorkflow.id,
    invalidateSubmissionQueries: refetchSubmission
  });

  const uploadICMJEFields = new GenericFormFields(authorUploadICMJE);

  const config = {
    ICMJEFormFile: uploadICMJEFields.getFieldConfig(
      'AuthorICMJEFormFile'
    ) as CustomFormFieldDefinition
  };

  const schema = generateValidationSchema(config);

  const methods = useForm({
    defaultValues: {
      authorICMJEForm: {
        file: {},
        isBulkUpload: false
      }
    },
    resolver: ajvResolver(schema)
  });
  const { handleSubmit, getValues, register } = methods;

  const uploadICMJEForm = async (payload: unknown, email: string) => {
    const res = await authorUploadICMJEMutation.mutateAsync(payload);
    const authorName = icmjeFormList?.filter(author => author.email === email);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: `${authorName?.[0]?.name}'s Forms And Disclosures Saved`,
        description: 'You can continue adding form and submit them when ready.'
      });
      onClose();
    } else {
      toast({
        variant: 'error',
        title: 'Error uploading Forms and Disclosures'
      });
    }
  };

  const submit = async () => {
    const formValues = getValues();

    if (formValues.authorICMJEForm.isBulkUpload === true) {
      if (!icmjeFormList?.length) return;

      for (const icmje of icmjeFormList) {
        const payload = {
          ...formValues,
          authorICMJEForm: {
            ...formValues.authorICMJEForm,
            email: icmje.email,
            isSubmittingAuthor: icmje.isSubmittingAuthor,
            isBulkUpload: true
          }
        };

        try {
          await uploadICMJEForm(payload, icmje.email);
        } catch {
          toast({
            variant: 'error',
            title: `Error uploading Forms and Disclosures for ${icmje.email}`
          });
        }
      }
    } else {
      const payload = {
        ...formValues,
        authorICMJEForm: {
          ...formValues.authorICMJEForm,
          email: authorEmail,
          isSubmittingAuthor: authorStatus
        }
      };
      await uploadICMJEForm(payload, authorEmail);
    }
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} width="720px">
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)}>
          <Flex padding={8} gap={6} flexDirection="column" overflow="auto">
            {config.ICMJEFormFile && (
              <CustomFormFieldComponent config={config.ICMJEFormFile} />
            )}
            <Checkbox
              id="uploadForAll"
              ariaLabel="UploadForAll"
              {...register('authorICMJEForm.isBulkUpload')}
            >
              Apply same for all the other authors
            </Checkbox>
            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={authorUploadICMJEMutation.isPending}
              >
                Upload
              </Button>
            </Flex>
          </Flex>
        </form>
      </FormProvider>
    </Modal>
  );
};
